import type { Page, Provider } from '@cabgo/shared';
import { Field, Select } from '@cabgo/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { RouteBoundary } from '../../components/RouteBoundary';
import { apiGet, getActiveOrg, setActiveOrg } from '../../lib/api';
import { hasCapability, isAdminRole, useAuth } from '../../lib/auth';

type NavItem = {
  to: string;
  labelKey: string;
  requiredCapability?: string;
  /** Platform-wide surfaces the server only grants to SUPER_ADMIN. */
  superAdminOnly?: boolean;
  adminOnly?: boolean;
};

type NavGroup = { key: string; labelKey: string; items: NavItem[] };

const NAV_GROUPS: NavGroup[] = [
  {
    key: 'organization',
    labelKey: 'navGroupOrganization',
    items: [
      { to: 'org', labelKey: 'org' },
      { to: 'org-users', labelKey: 'orgUsers' },
      { to: 'partner-connections', labelKey: 'partnerConnections', adminOnly: true },
      {
        // Least-privilege provider onboarding (issue #415).
        to: 'provider-onboarding',
        labelKey: 'providerOnboarding',
        requiredCapability: 'PROVIDER_ONBOARDING_CREATE',
      },
      { to: 'diagnostics', labelKey: 'diagnosticsTitle', requiredCapability: 'DIAGNOSTICS_VIEW' },
      { to: 'permissions', labelKey: 'permissions', requiredCapability: 'SETTINGS_PERMISSIONS_VIEW' },
      {
        to: 'agent-login-codes',
        labelKey: 'agentLoginCodes',
        requiredCapability: 'AGENT_LOGIN_CODE_MANAGE',
        adminOnly: true,
      },
    ],
  },
  {
    key: 'service',
    labelKey: 'navGroupService',
    items: [
      { to: 'service-groups', labelKey: 'serviceGroups' },
      { to: 'vehicles', labelKey: 'vehicles' },
      { to: 'service-areas', labelKey: 'serviceAreas' },
      { to: 'passenger-groups', labelKey: 'passengerGroups' },
      { to: 'equipment', labelKey: 'equipment' },
    ],
  },
  {
    key: 'policy',
    labelKey: 'navGroupPolicy',
    items: [
      { to: 'dispatch-policy', labelKey: 'dispatchPolicy' },
      { to: 'booking-amendments', labelKey: 'amendment_title', adminOnly: true },
      {
        to: 'driver-document-policy',
        labelKey: 'driverDocumentPolicy',
        requiredCapability: 'SETTINGS_DRIVER_DOCUMENT_POLICY_VIEW',
      },
      {
        to: 'driver-application-policy',
        labelKey: 'driverApplicationPolicy',
        requiredCapability: 'SETTINGS_DRIVER_APPLICATION_POLICY_VIEW',
      },
    ],
  },
  {
    key: 'reasons',
    labelKey: 'navGroupReasons',
    items: [
      { to: 'reasons', labelKey: 'reasons' },
      { to: 'rating-reasons', labelKey: 'ratingReasons' },
    ],
  },
  {
    // Platform-wide archive views — SUPER_ADMIN only (server enforces too).
    key: 'deleted-accounts',
    labelKey: 'navGroupDeletedAccounts',
    items: [
      { to: 'deletion-requests', labelKey: 'deletionRequests', superAdminOnly: true },
      { to: 'deleted-agents', labelKey: 'deletedAgents', superAdminOnly: true },
      { to: 'deleted-passengers', labelKey: 'deletedPassengers', superAdminOnly: true },
    ],
  },
];

/** Standalone item below the groups — SUPER_ADMIN only (rendered outside the groups). */
const SYSTEM_ITEM: NavItem = { to: 'system', labelKey: 'systemSettings' };

/** Group holding the active route — collapsed groups of the current page must stay findable. */
const groupOfActiveRoute = (pathname: string): NavGroup | undefined => {
  const segments = pathname.split('/').filter(Boolean);
  // Match the segment right after /settings/ so nested detail routes keep their group open.
  const route = segments[segments.indexOf('settings') + 1];
  return NAV_GROUPS.find((group) => group.items.some((item) => item.to === route));
};

/** Settings: secondary nav list (232px, per design) + section content. */
export function SettingsLayout() {
  const { t } = useTranslation('settings');
  const { me } = useAuth();
  const qc = useQueryClient();
  const [activeOrg, setActiveOrgState] = useState(getActiveOrg);
  const providerId = (me?.role === 'SUPER_ADMIN' ? activeOrg : null) ?? me?.providerId ?? '';
  const superAdmin = me?.role === 'SUPER_ADMIN';
  // SUPER_ADMIN bypasses capability rows server-side, so the nav must not
  // depend on /me expanding every capability for them.
  const meetsCapability = (required?: string) => !required || superAdmin || hasCapability(me, required);
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  // Collapsed by default; the group owning the active route opens itself so the
  // current page is always reachable in the a11y tree and stays open across navigations.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const active = groupOfActiveRoute(location.pathname);
    return new Set(active ? [active.key] : []);
  });
  useEffect(() => {
    const active = groupOfActiveRoute(location.pathname);
    if (active) setOpenGroups((prev) => (prev.has(active.key) ? prev : new Set(prev).add(active.key)));
  }, [location.pathname]);
  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Org switcher per design — SUPER_ADMIN sees every org and can switch the
  // whole console's context; everyone else sees their own org, fixed.
  const { data: org } = useQuery({
    queryKey: ['settings', 'org'],
    queryFn: () => apiGet<Provider>('/settings/org'),
  });
  const { data: orgs } = useQuery({
    queryKey: ['settings', 'orgs'],
    queryFn: () => apiGet<Page<Provider>>('/settings/orgs'),
    enabled: superAdmin,
  });

  const switchOrg = (orgId: string) => {
    setActiveOrg(orgId === me?.providerId ? null : orgId);
    setActiveOrgState(orgId === me?.providerId ? null : orgId);
    // every provider-scoped query is now answering for a different org
    qc.invalidateQueries();
  };

  const options = superAdmin
    ? (orgs?.data ?? []).map((o) => ({ value: o.id, label: o.name }))
    : org
      ? [{ value: org.id, label: org.name }]
      : [];

  return (
    <div className="content">
      <button
        type="button"
        className="navlist-toggle--mobile"
        aria-expanded={navOpen}
        aria-controls="settings-nav"
        onClick={() => setNavOpen((open) => !open)}
      >
        <Menu size={18} />
        {t('menu')}
      </button>
      {navOpen && <div className="navlist-overlay" aria-hidden="true" onClick={() => setNavOpen(false)} />}
      <nav id="settings-nav" className={`navlist ${navOpen ? 'is-open' : ''}`} aria-label={t('title')}>
        <div className="navlist__header">
          <span className="navlist__title">{t('title')}</span>
          <button
            type="button"
            className="navlist__close"
            aria-label={t('common:close')}
            onClick={() => setNavOpen(false)}
          >
            <X size={20} />
          </button>
        </div>
        {options.length > 0 && (
          <Field label={t('org')}>
            {(id) => (
              <Select
                id={id}
                value={providerId}
                options={options}
                onChange={(value) => (superAdmin ? switchOrg(value) : undefined)}
                style={{ marginBottom: 12 }}
              />
            )}
          </Field>
        )}
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter(
            (item) =>
              (!item.superAdminOnly || superAdmin) &&
              (!item.adminOnly || isAdminRole(me?.role)) &&
              meetsCapability(item.requiredCapability),
          );
          // A group with nothing visible for this role disappears entirely.
          if (items.length === 0) return null;
          const open = openGroups.has(group.key);
          const panelId = `settings-nav-group-${group.key}`;
          return (
            <div key={group.key}>
              <button
                type="button"
                className="navlist__group-toggle"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => toggleGroup(group.key)}
              >
                {t(group.labelKey)}
                <ChevronDown size={16} aria-hidden="true" />
              </button>
              {open && (
                <div id={panelId} className="navlist__group-items">
                  {items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) => `navlist__item ${isActive ? 'navlist__item--active' : ''}`}
                      onClick={() => setNavOpen(false)}
                    >
                      {t(item.labelKey)}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {superAdmin && (
          <NavLink
            to={SYSTEM_ITEM.to}
            className={({ isActive }) => `navlist__item ${isActive ? 'navlist__item--active' : ''}`}
            onClick={() => setNavOpen(false)}
          >
            {t(SYSTEM_ITEM.labelKey)}
          </NavLink>
        )}
      </nav>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <RouteBoundary>
          <Outlet context={{ providerId }} />
        </RouteBoundary>
      </div>
    </div>
  );
}
